import type { Translations } from '@/i18n/types';

const nl: Translations = {
  /* ── Common ───────────────────────────────────────────────── */
  'common.add': 'Toevoegen',
  'common.save': 'Opslaan',
  'common.create': 'Aanmaken',
  'common.edit': 'Bewerken',
  'common.delete': 'Verwijderen',
  'common.cancel': 'Annuleren',
  'common.close': 'Sluiten',
  'common.back': 'Terug',
  'common.skip': 'Overslaan',
  'common.search': 'Zoeken',
  'common.retry': 'Opnieuw proberen',
  'common.reloadPage': 'Pagina herladen',
  'offline.title': 'Je bent offline',
  'offline.message': 'Je bent momenteel niet verbonden met het internet.',
  'common.loading': 'Laden…',
  'common.optional': '(optioneel)',
  'common.yes': 'Ja',
  'common.no': 'Nee',
  'common.none': '— Geen —',
  'common.notSet': '— Niet ingesteld —',
  'common.deleting': 'Verwijderen…',
  'common.applying': 'Toepassen…',
  'common.closeDialog': 'Venster sluiten',
  'common.prev': 'Vorige',
  'common.next': 'Volgende',

  /* ── Filter UI ────────────────────────────────────────────── */
  'filter.add': 'Filter toevoegen',
  'filter.allActive': 'Alle filters actief',
  'filter.clearAll': 'Alles wissen',
  'filter.clear': 'Wissen',
  'filter.apply': 'Toepassen',
  'filter.cancel': 'Annuleren',
  'filter.condition': 'Voorwaarde',
  'filter.presets': 'Voorinstellingen',
  'filter.customRange': 'Aangepast bereik',
  'filter.from': 'Van',
  'filter.to': 'Tot',
  'filter.min': 'Min',
  'filter.max': 'Max',
  'filter.value': 'Waarde',
  'filter.searchOptions': 'Opties zoeken...',
  'filter.noOptions': 'Geen opties gevonden',
  'filter.loadingOptions': 'Opties laden...',

  /* ── Auth ─────────────────────────────────────────────────── */
  'auth.signIn': 'Inloggen',
  'auth.signInWithGoogle': 'Inloggen met Google',
  'auth.signInWithApple': 'Log in met Apple',
  'auth.or': 'of',
  'auth.signOut': 'Uitloggen',
  'auth.welcome': 'Welkom',
  'auth.subtitle': 'Kart Editor',
  'auth.signingIn': 'Inloggen...',

  /* ── Settings ───────────────────────────────────────────────── */
  'settings.title': 'Instellingen',
  'settings.account': 'Account',
  'settings.profile': 'Profiel',
  'settings.displayName': 'Weergavenaam',
  'settings.saveName': 'Naam opslaan',
  'settings.nameSaved': 'Naam succesvol bijgewerkt',
  'settings.linkedAccounts': 'Gekoppelde accounts',
  'settings.linkGoogle': 'Google-account koppelen',
  'settings.linkApple': 'Apple-account koppelen',
  'settings.unlink': 'Ontkoppelen',
  'settings.linked': 'Gekoppeld',
  'settings.notLinked': 'Niet gekoppeld',
  'settings.cannotUnlinkOnlyProvider': 'Er moet ten minste één inlogmethode gekoppeld blijven.',
  'settings.language': 'Taal',
  'settings.tracks': 'Circuits',
  'settings.categories': 'Categorieën',
  'settings.drivers': 'Rijder-aliassen',
  'settings.users': 'Gebruikers',
  'settings.userId': 'Gebruikers-ID',
  'settings.signOut': 'Uitloggen',
  'settings.connectedApps': 'Gekoppelde applicaties',
  'settings.connectedAppsDesc': 'Beheer AI-assistenten, IDE\'s en tools die geautoriseerd zijn om via MCP toegang te krijgen tot je telemetriegegevens.',
  'settings.noConnectedApps': 'Nog geen gekoppelde applicaties. Autoriseer een MCP-client (zoals Claude Desktop of Cursor) om deze hier te zien.',
  'settings.authorizedOn': 'Geautoriseerd',
  'settings.lastUsed': 'Laatst gebruikt',
  'settings.neverUsed': 'Nooit',
  'settings.revoke': 'Intrekken',
  'settings.revokeAppConfirm': 'Weet je zeker dat je de toegang voor deze applicatie wilt intrekken? Actieve sessies stoppen direct met werken.',
  'settings.appRevokedSuccess': 'Applicatietoegang succesvol ingetrokken',
  'settings.copyKey': 'Snippet kopiëren',
  'settings.keyCopied': 'Gekopieerd naar klembord',
  'settings.mcpConfigSnippet': 'MCP-clientconfiguratie',
  'settings.mcpConfigDesc': 'Voeg deze configuratie toe aan je Claude Desktop (claude_desktop_config.json) of Cursor MCP-instellingen. Authenticatie verloopt automatisch via je browser:',
  'settings.deleteAccount': 'Account verwijderen',
  'settings.deleteAccountDesc': 'Verwijder je account en alle bijbehorende sessies, telemetriegegevens, opgeslagen analyses, materiaal, kart-setups en gedeelde items definitief.',
  'settings.deleteAccountConfirmTitle': 'Account definitief verwijderen?',
  'settings.deleteAccountConfirmDesc': 'Deze actie kan niet ongedaan worden gemaakt. Al je eigen sessies, telemetriegegevens, opgeslagen analyses, materiaal, kart-setups, teams en gedeelde sessies worden direct en definitief verwijderd.',
  'settings.deleteAccountConfirmPrompt': 'Typ ter bevestiging "{phrase}" in het veld hieronder:',
  'settings.deleteAccountConfirmPhrase': 'mijn account verwijderen',
  'settings.deleteAccountConfirmButton': 'Mijn account verwijderen',

  /* ── Common fields ────────────────────────────────────────── */
  'common.brand': 'Merk',
  'common.type': 'Type',
  'common.nr': 'Nr.#',
  'common.nrInfo': 'Volgnummer om dit item te identificeren.',
  'common.year': 'Jaar',
  'common.stiffness': 'Stijfheid',
  'common.width': 'Breedte',
  'common.position': 'Positie',

  /* ── Tabs ──────────────────────────────────────────────────── */
  'tab.engines': 'Motoren',
  'tab.tyres': 'Banden',
  'tab.chassis': 'Chassis',
  'tab.carburetors': 'Carburateurs',
  'tab.axles': 'Assen',
  'tab.hubs': 'Naven',
  'tab.spark_plugs': 'Bougies',
  'tab.tracks': 'Circuits',
  'tab.categories': 'Categorieën',
  'tab.drivers': 'Rijders',
  'tab.races': 'Races',
  'tab.users': 'Gebruikers',
  'tab.settings': 'Instellingen',

  /* ── Navigation ────────────────────────────────────────────── */
  'nav.administration': 'Beheer',

  /* ── CRUD ──────────────────────────────────────────────────── */
  'crud.requiredField': '{field} is verplicht',
  'crud.updated': '{entity} bijgewerkt',
  'crud.created': '{entity} aangemaakt',
  'crud.deleted': '{entity} verwijderd',
  'crud.confirmDelete': '{entity} "{name}" verwijderen?\nDit kan niet ongedaan worden gemaakt.',
  'crud.emptyState': 'Nog geen {title}. Voeg hierboven je eerste {entity} toe.',
  'crud.editEntity': '{entity} bewerken',
  'crud.newEntity': 'Nieuwe {entity}',

  /* ── Entities ──────────────────────────────────────────────── */
  'entity.engine': 'Motor',
  'entity.tyre': 'Band',
  'entity.chassis': 'Chassis',
  'entity.carburetor': 'Carburateur',
  'entity.axle': 'As',
  'entity.hub': 'Naaf',
  'entity.spark_plug': 'Bougie',
  'entity.track': 'Circuit',
  'entity.category': 'Categorie',
  'entity.driver': 'Rijder',

  /* ── Import Dialog ─────────────────────────────────────────── */
  'import.title': 'Race-PDF\'s importeren',
  'import.uploadDesc': 'Upload RMC-raceresultaat-PDF\'s. Ze worden uitgelezen en ter controle weergegeven voordat ze worden geïmporteerd.',
  'import.dragDropPdf': 'Sleep PDF-bestanden hierheen',
  'import.orBrowse': 'of',
  'import.browseFiles': 'blader door bestanden',
  'import.onlyPdfAccepted': 'Alleen PDF-bestanden worden geaccepteerd.',
  'import.filesSelected': '{n} bestand(en) geselecteerd',
  'import.upload': 'Uploaden & uitlezen',
  'import.uploadingPct': 'Uploaden… {n}%',
  'import.parseFailed': 'Kan het antwoord niet verwerken.',
  'import.uploadFailed': 'Uploaden mislukt. Probeer het opnieuw.',
  'import.connectionLost': 'Verbinding verbroken.',
  'import.uploadAborted': 'Uploaden afgebroken.',
  'import.reviewDesc': 'Controleer de uitgelezen sessies. Deselecteer sessies die je niet wilt importeren.',
  'import.selectAll': 'Alles selecteren',
  'import.deselectAll': 'Alles deselecteren',
  'import.existsInDb': 'Bestaat al — wordt overschreven',
  'import.entriesCount': '{n} deelnemers',
  'import.submit': 'Importeren',
  'import.submitting': 'Sessies importeren…',
  'import.importedCount': '{n} sessie(s) geïmporteerd.',
  'import.successMessage': 'Sessies succesvol geïmporteerd!',
  'import.noNewSessions': 'Er zijn geen nieuwe sessies geïmporteerd.',
  'import.imported': 'Geïmporteerd',
  'import.skipped': 'Overgeslagen',
  'import.errors': 'Fouten',
  'import.importButton': 'PDF\'s importeren',

  /* ── Tracks ─────────────────────────────────────────────────── */
  'tracks.name': 'Circuitnaam',
  'tracks.latitude': 'Breedtegraad',
  'tracks.longitude': 'Lengtegraad',
  'tracks.searchAddress': 'Zoek een adres…',
  'tracks.coordinates': 'Coördinaten',
  'tracks.clickToAdjust': 'Klik op de kaart om de pin aan te passen',
  'tracks.tabLocation': 'Locatie',
  'tracks.tabAliases': 'Aliassen',
  'tracks.sessionTracks': 'Sessiecircuits',
  'tracks.raceEventTracks': 'Race-evenementcircuits',
  'tracks.noSessionTracks': 'Geen sessiecircuits beschikbaar',
  'tracks.noRaceEventTracks': 'Geen race-evenementcircuits beschikbaar',
  'tracks.linkedAliases': '{sessions} sessiecircuits · {races} racecircuits',
  'tracks.satelliteImage': 'Satellietbeeld',
  'tracks.fetchImage': 'Satellietbeeld ophalen',
  'tracks.refetchImage': 'Satellietbeeld opnieuw ophalen',
  'tracks.fetchingImage': 'Beeld ophalen…',
  'tracks.imageFetched': 'Satellietbeeld succesvol opgehaald',
  'tracks.imageFetchFailed': 'Ophalen van satellietbeeld mislukt',
  'tracks.noImage': 'Geen satellietbeeld',
  'tracks.imageZoom': 'Zoomniveau',
  'tracks.autoZoom': 'Automatisch zoomen',

  /* ── Categories ─────────────────────────────────────────────── */
  'categories.name': 'Naam',
  'categories.aliases': 'Categorie-aliassen',
  'categories.noAliases': 'Geen categorienamen beschikbaar',
  'categories.linkedAliases': '{count} aliassen gekoppeld',

  /* ── Races ──────────────────────────────────────────────────── */
  'races.title': 'Racesessies',
  'races.loading': 'Racesessies laden…',
  'races.empty': 'Nog geen racesessies. Importeer race-PDF\'s om te beginnen.',
  'races.loadFailed': '⚠ Laden van racesessies mislukt: {error}',
  'races.date': 'Datum',
  'races.track': 'Circuit',
  'races.sessionType': 'Type',
  'races.category': 'Categorie',
  'races.entries': 'Deelnemers',
  'races.laps': 'Ronden',
  'races.championship': 'Kampioenschap',
  'races.deleteConfirmTitle': 'Racesessie verwijderen',
  'races.deleteConfirmMessage': 'Weet je zeker dat je de sessie <strong>{type}</strong> op <strong>{track}</strong> op <strong>{date}</strong> wilt verwijderen? Hiermee worden alle deelnemers en rondegegevens definitief verwijderd.',
  'races.deleteConfirmButton': 'Sessie verwijderen',
  'races.deleted': 'Racesessie verwijderd',
  'races.detailTitle': 'Racesessie',
  'races.detailSubtitle': '{type} · {track} · {date}',
  'races.position': 'Pos',
  'races.kart': 'Kart',
  'races.driver': 'Rijder',
  'races.firstName': 'Voornaam',
  'races.lastName': 'Achternaam',
  'races.nationality': 'Nat',
  'races.grid': 'Grid',
  'races.bestLap': 'Beste ronde',
  'races.totalTime': 'Totale tijd',
  'races.penalty': 'Straf',
  'races.gap': 'Verschil',
  'races.status': 'Status',
  'races.deleteEntry': 'Deelnemer verwijderen',
  'races.entryDeleted': 'Deelnemer verwijderd',
  'races.saved': 'Racesessie opgeslagen',
  'races.searchPlaceholder': 'Zoek races… bijv. Castelletto of type:race',
  'races.querySyntaxHelp': 'Hulp bij zoeksyntax',
  'races.sessionCount': '{count} sessies',
  'races.sessionCountSingular': '1 sessie',

  /* ── Race Search Help Dialog ──────────────────────────────── */
  'raceSearchHelp.title': 'Zoeksyntax voor races',
  'raceSearchHelp.subtitle': 'Filter racesessies met veld:waarde-paren. Meerdere termen worden gecombineerd met EN.',
  'raceSearchHelp.bareText': '(vrije tekst)',
  'raceSearchHelp.bareTextDesc': 'Komt overeen met circuitnaam of kampioenschapsnaam die de tekst bevat.',
  'raceSearchHelp.track': 'track',
  'raceSearchHelp.trackDesc': 'Komt overeen met circuitnaam die de waarde bevat.',
  'raceSearchHelp.championship': 'championship',
  'raceSearchHelp.championshipDesc': 'Komt overeen met kampioenschapsnaam die de waarde bevat.',
  'raceSearchHelp.category': 'category',
  'raceSearchHelp.categoryDesc': 'Komt overeen met categorienaam die de waarde bevat.',
  'raceSearchHelp.type': 'type',
  'raceSearchHelp.typeDesc': 'Komt overeen met sessietype of label (bijv. race, qualifying, heat).',
  'raceSearchHelp.entries': 'entries',
  'raceSearchHelp.entriesDesc': 'Filter op aantal deelnemers. Ondersteunt =, >, >=, <, <= en bereiken.',
  'raceSearchHelp.laps': 'laps',
  'raceSearchHelp.lapsDesc': 'Filter op totaal aantal ronden. Ondersteunt vergelijkingsoperatoren en bereiken.',
  'raceSearchHelp.season': 'season',
  'raceSearchHelp.seasonDesc': 'Filter op kampioenschapsseizoen (jaar). Ondersteunt vergelijkingsoperatoren.',
  'raceSearchHelp.from': 'from',
  'raceSearchHelp.fromDesc': 'Races op of na een datum. Accepteert JJJJ-MM-DD of relatief: -7d, -2w, -3m, -1y.',
  'raceSearchHelp.to': 'to',
  'raceSearchHelp.toDesc': 'Races op of vóór een datum. Zelfde formaten als "from".',
  'raceSearchHelp.operatorsTitle': 'Operatoren & logica',
  'raceSearchHelp.numericComparisons': 'Numerieke vergelijkingen:',
  'raceSearchHelp.range': 'Bereik:',
  'raceSearchHelp.rangeDesc': '— komt overeen met waarden tussen 5 en 20 (inclusief).',
  'raceSearchHelp.booleanLogic': 'Booleaanse logica:',
  'raceSearchHelp.booleanLogicDesc': 'Termen worden impliciet gecombineerd met',
  'raceSearchHelp.quotes': 'Aanhalingstekens:',
  'raceSearchHelp.quotesDesc': 'Zet waarden met spaties tussen dubbele aanhalingstekens, bijv.',
  'raceSearchHelp.examplesTitle': 'Voorbeeldzoekopdrachten',
  'raceSearchHelp.example1Desc': 'Racesessies op Castelletto',
  'raceSearchHelp.example2Desc': 'RMC-kampioenschapsraces van het afgelopen jaar',
  'raceSearchHelp.example3Desc': 'Races met 20+ deelnemers en 5+ ronden',

  /* ── Pagination ─────────────────────────────────────────────── */
  'pagination.eventsFound': '{n} evenement(en) gevonden',

  /* ── Drivers ──────────────────────────────────────────────── */
  'drivers.canonicalDriver': 'Hoofdrijder',
  'drivers.aliases': 'Aliassen',
  'drivers.aliasesHeader': 'Aliassen',
  'drivers.raceDrivers': 'Racerijders',
  'drivers.sessionDrivers': 'Sessierijders',
  'drivers.noRaceDrivers': 'Geen racerijders beschikbaar',
  'drivers.noSessionDrivers': 'Geen sessierijders beschikbaar',
  'drivers.selectDriver': 'Selecteer een rijder…',
  'drivers.searchDrivers': 'Zoek rijders…',
  'drivers.createTitle': 'Nieuwe rijder-alias',
  'drivers.editTitle': 'Rijder-alias bewerken',
  'drivers.stepCanonical': 'Selecteer rijder',
  'drivers.stepAliases': 'Selecteer aliassen',

  /* ── Users ──────────────────────────────────────────────── */
  'users.title': 'Gebruikers',
  'users.empty': 'Geen gebruikers gevonden',
  'users.loading': 'Gebruikers laden...',
  'users.searchPlaceholder': 'Zoek gebruikers...',
  'users.name': 'Naam',
  'users.email': 'E-mail',
  'users.user': 'Gebruiker',
  'users.admin': 'Beheerder',
  'users.owner': 'Eigenaar',
  'users.promote': 'Promoveren',
  'users.demote': 'Degraderen',
  'users.promoted': '{name} gepromoveerd tot beheerder',
  'users.demoted': '{name} verwijderd als beheerder',
  'users.cannotDemoteOwner': 'De eigenaar kan niet worden gedegradeerd',
  'users.cannotDemoteSelf': 'Je kunt jezelf niet degraderen',
  'users.confirmPromote': '{name} promoveren tot beheerder? Deze gebruiker krijgt volledige toegang tot de editor.',
  'users.confirmDemote': 'Beheerderstoegang intrekken voor {name}? Deze gebruiker kan de editor niet langer gebruiken.',
  'users.accessDenied': 'Toegang geweigerd',
  'users.accessDeniedMessage': 'Je hebt geen beheerdersrechten. Neem contact op met een beheerder om toegang aan te vragen.',
  'users.joinedAt': 'Lid sinds',
  'users.lastLogin': 'Laatste login',
  'users.count': '{count} gebruikers',
  'users.countSingular': '1 gebruiker',

  /* ── User Search Help Dialog ────────────────────────────── */
  'userSearchHelp.title': 'Gebruikers zoeken',
  'userSearchHelp.subtitle': 'Filter de gebruikerslijst met veldspecifieke of vrije zoekopdrachten.',
  'userSearchHelp.bareText': 'Vrije tekst',
  'userSearchHelp.bareTextDesc': 'Zoekt in zowel naam als e-mailadres.',
  'userSearchHelp.name': 'name:',
  'userSearchHelp.nameDesc': 'Filter op weergavenaam van de gebruiker.',
  'userSearchHelp.email': 'email:',
  'userSearchHelp.emailDesc': 'Filter op e-mailadres.',
  'userSearchHelp.login': 'login:',
  'userSearchHelp.loginDesc': 'Filter op datum van laatste login. Ondersteunt enkele waarden (-7d) of bereiken (-30d..today). Gebruik relatieve datums (-7d, -2w, -3m), absolute datums (2026-01-15) of today.',
  'userSearchHelp.created': 'created:',
  'userSearchHelp.createdDesc': 'Filter op aanmaakdatum van het account. Ondersteunt enkele waarden (-30d) of bereiken (-1y..today). Gebruik relatieve datums (-7d, -2w, -3m), absolute datums (2026-01-15) of today.',
  'userSearchHelp.examplesTitle': 'Voorbeelden',
  'userSearchHelp.example1Desc': 'Vind gebruikers met de naam Yanick',
  'userSearchHelp.example2Desc': 'Vind gebruikers met een Gmail-adres',
  'userSearchHelp.example3Desc': 'Vind gebruikers die de afgelopen 7 dagen zijn ingelogd',
  'userSearchHelp.example4Desc': 'Vind gebruikers die binnen een datumbereik zijn aangemaakt',

  /* ── Bugs tab ───────────────────────────────────────────────── */
  'tab.bugs': 'Bugs',
  'bugs.title': 'Bugrapporten',
  'bugs.empty': 'Geen bugrapporten gevonden.',
  'bugs.loading': 'Bugrapporten laden…',
  'bugs.searchPlaceholder': 'Zoek bugrapporten…',
  'bugs.reporter': 'Melder',
  'bugs.description': 'Beschrijving',
  'bugs.pageUrl': 'Pagina-URL',
  'bugs.status': 'Status',
  'bugs.statusOpen': 'Open',
  'bugs.statusResolved': 'Opgelost',
  'bugs.createdAt': 'Aangemaakt',
  'bugs.resolvedAt': 'Opgelost op',
  'bugs.resolve': 'Oplossen',
  'bugs.reopen': 'Heropenen',
  'bugs.resolved': 'Bugrapport opgelost.',
  'bugs.reopened': 'Bugrapport heropend.',
  'bugs.count': '{count} rapporten',
  'bugs.countSingular': '1 rapport',
  'bugs.detailTitle': 'Bugrapport',
  'bugs.userAgent': 'User-Agent',
  'bugs.screenSize': 'Schermgrootte',
  'bugs.appLanguage': 'Taal',
  'bugs.copy': 'Tekst kopiëren',
  'bugs.copied': 'Gekopieerd naar klembord',

  /* ── Bug search help ────────────────────────────────────────── */
  'bugSearchHelp.title': 'Zoeksyntax',
  'bugSearchHelp.subtitle': 'Gebruik deze voorvoegsels om je zoekopdracht te verfijnen.',
  'bugSearchHelp.bareText': 'vrije tekst',
  'bugSearchHelp.bareTextDesc': 'Zoekt in beschrijving en naam van de melder.',
  'bugSearchHelp.status': 'status:open',
  'bugSearchHelp.statusDesc': 'Filter op status (open of resolved).',
  'bugSearchHelp.email': 'email:waarde',
  'bugSearchHelp.emailDesc': 'Filter op e-mailadres van de melder.',
  'bugSearchHelp.examplesTitle': 'Voorbeelden',
  'bugSearchHelp.example1Desc': 'Vind alle openstaande bugrapporten.',
  'bugSearchHelp.example2Desc': 'Vind bugrapporten van Gmail-gebruikers.',

  /* ── Error auto-reporting & Error Boundary ─────────────────────────── */
  'error.autoReported': 'Er is een fout opgetreden en deze is automatisch gemeld.',
  'errorBoundary.title': 'Er ging iets mis',
  'errorBoundary.message': 'Er is een onverwachte fout opgetreden en deze is gemeld aan de server.',
  'errorBoundary.reload': 'Pagina herladen',
  'errorBoundary.reset': 'Opnieuw proberen',
  'errorBoundary.showDetails': 'Foutdetails tonen',
  'errorBoundary.hideDetails': 'Foutdetails verbergen',

  /* ── Profiles ───────────────────────────────────────────────── */
  'tab.profiles': 'Profielen',
  'profiles.engineProfiles': 'Motorprofielen',
  'profiles.tyreProfiles': 'Bandenprofielen',
  'profiles.addEngineProfile': 'Motorprofiel toevoegen',
  'profiles.editEngineProfile': 'Motorprofiel bewerken',
  'profiles.addTyreProfile': 'Bandenprofiel toevoegen',
  'profiles.editTyreProfile': 'Bandenprofiel bewerken',
  'profiles.brand': 'Merk',
  'profiles.type': 'Type',
  'profiles.compound': 'Compound',
  'profiles.compoundDry': 'Droog',
  'profiles.compoundWet': 'Nat',
  'profiles.deleteBlocked': 'Kan niet verwijderen — nog in gebruik door materiaal.',
  'profiles.emptyEngineProfiles': 'Nog geen motorprofielen.',
  'profiles.emptyTyreProfiles': 'Nog geen bandenprofielen.',
  'entity.engineProfile': 'Motorprofiel',
  'entity.tyreProfile': 'Bandenprofiel',

  /* ── Track Shape Studio ────────────────────────────────────── */
  'trackStudio.title': 'Circuitvorm & Annotatie Studio',
  'trackStudio.subtitle': 'Ontwerp circuitgeometrie, bewerk splines en plaats bocht- en sectorannotaties',
  'trackStudio.openStudio': 'Circuitgeometrie & Annotaties',
  'trackStudio.modeSpline': 'Geometrie & Splines',
  'trackStudio.modeCorners': 'Bochten',
  'trackStudio.modeSectors': 'Sectoren',
  'trackStudio.modeSegments': 'Segmenten',
  'trackStudio.bakeSegments': '200 segmenten genereren',
  'trackStudio.rebakeSegments': '200 segmenten opnieuw genereren',
  'trackStudio.segmentsBakedSuccess': '{count} circuitsegmenten succesvol gegenereerd',
  'trackStudio.segmentsCount': '{count} segmenten',
  'trackStudio.segmentsStatusBaked': '200 segmenten gegenereerd',
  'trackStudio.segmentsStatusNotBaked': 'Nog niet gegenereerd',
  'trackStudio.segmentsHint': 'Verdeelt de middellijn van het circuit in 200 gelijke statische GPS-segmenten vanaf de start-/finishlijn voor uiterst nauwkeurige rondevergelijkingen.',
  'trackStudio.segmentDetails': 'Segmentdetails',
  'trackStudio.segmentIndex': 'Segment #{index}',
  'trackStudio.avgSegmentLength': 'Gem. segmentlengte',
  'trackStudio.searchSegments': 'Segmenten zoeken...',
  'trackStudio.modeAnnotations': 'Bocht- & sectorannotaties',
  'trackStudio.cornersCount': 'Circuitbochten ({count})',
  'trackStudio.sectorsCount': 'Tijdsectoren ({count})',
  'trackStudio.clickCornersHint': 'Klik ergens op het circuitlint om een bocht toe te voegen. Versleep de apex om deze te verplaatsen of pas het instuur- en uitacceleratiepunt aan.',
  'trackStudio.clickSectorsHint': 'Klik ergens op het circuitlint om een sectormarkering te plaatsen. Versleep markeringen om ze langs het circuit te verplaatsen.',
  'trackStudio.trackLength': 'Circuitlengte: {length}m',
  'trackStudio.trackWidth': 'Baanbreedte',
  'trackStudio.defaultTrackWidth': 'Standaard baanbreedte',
  'trackStudio.pointWidth': 'Puntbreedte',
  'trackStudio.customWidth': 'Aangepaste breedte voor dit punt',
  'trackStudio.useDefaultWidth': 'Gebruik standaard ({width}m)',
  'trackStudio.resetPointWidth': 'Herstel naar standaard',
  'trackStudio.segmentWidth': 'Segmentbreedte-tool',
  'trackStudio.segmentWidthDesc': 'Pas de breedte aan of maak vloeiende breedte-overgangen tussen punten',
  'trackStudio.fromPoint': 'Van punt',
  'trackStudio.toPoint': 'Tot punt',
  'trackStudio.startWidth': 'Startbreedte',
  'trackStudio.endWidth': 'Eindbreedte',
  'trackStudio.transitionMode': 'Overgangsmodus',
  'trackStudio.modeConstant': 'Constante breedte',
  'trackStudio.modeGradient': 'Vloeiende overgang (verloop)',
  'trackStudio.applySegment': 'Breedte toepassen op segment',
  'trackStudio.resetSegment': 'Segmentbreedte herstellen',
  'trackStudio.segmentApplied': 'Breedte bijgewerkt voor punten {from} t/m {to}',
  'trackStudio.controlPoints': 'Controlepunten middellijn ({count})',
  'trackStudio.addPointHint': 'Klik op de lijn of het canvas om controlepunten in te voegen. Versleep punten om de vorm aan te passen.',
  'trackStudio.selectedPoint': 'Geselecteerd punt #{index}',
  'trackStudio.deletePoint': 'Punt verwijderen',
  'trackStudio.evenSpacePoints': 'Punten gelijkmatig verdelen',
  'trackStudio.reverseDirection': 'Circuitrichting omdraaien',
  'trackStudio.layerSatellite': 'Satellietbeeld',
  'trackStudio.layerCenterline': 'Middellijn-spline',
  'trackStudio.layerBoundaries': 'Baangrenzen',
  'trackStudio.layerAnnotations': 'Annotaties & markeringen',
  'trackStudio.undo': 'Ongedaan maken (Cmd+Z)',
  'trackStudio.redo': 'Opnieuw uitvoeren (Cmd+Shift+Z)',
  'trackStudio.reset': 'Wijzigingen herstellen',
  'trackStudio.saveSuccess': 'Circuitvorm en annotaties succesvol opgeslagen!',
  'trackStudio.autoSaveSuccess': 'Circuitvorm en annotaties automatisch opgeslagen',
  'trackStudio.saveError': 'Opslaan van circuitvorm mislukt: {error}',
  'trackStudio.noCenterline': 'Klik op het satellietbeeld om het eerste controlepunt voor de middellijn van het circuit te plaatsen.',
  'trackStudio.annotations': 'Circuitannotaties ({count})',
  'trackStudio.addCorner': 'Bocht toevoegen (T{next})',
  'trackStudio.addSector': 'Sectormarkering toevoegen',
  'trackStudio.addFinishLine': 'Start-/finishlijn instellen',
  'trackStudio.cornerName': 'Bochtnaam',
  'trackStudio.cornerDirection': 'Bochtrichting',
  'trackStudio.directionLeft': 'Bocht naar links',
  'trackStudio.directionRight': 'Bocht naar rechts',
  'trackStudio.entry': 'Ingang (insturen / remmen)',
  'trackStudio.apex': 'Apex (snijpunt)',
  'trackStudio.exit': 'Uitgang (uitkomen / volgas)',
  'trackStudio.deleteAnnotation': 'Annotatie verwijderen',
  'trackStudio.noAnnotations': 'Nog geen annotaties aangemaakt. Klik op de circuitvorm om bochten, sectoren of de start-/finishlijn te plaatsen.',
  'trackStudio.finishLinePlaced': 'Finishlijn is actief op {pos}% van de ronde.',
  'trackStudio.closeConfirm': 'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je de Studio wilt sluiten?',
  'trackStudio.menuAddCorner': 'Bocht toevoegen',
  'trackStudio.menuAddSector': 'Sectormarkering toevoegen',
  'trackStudio.menuSetFinishLine': 'Start-/finishlijn instellen',
  'trackStudio.menuTrackPosition': 'Baanpositie',
  'trackStudio.clickTrackHint': 'Klik ergens op het circuitlint om een bocht, sectormarkering of de start-/finishlijn toe te voegen.',
  'trackStudio.drivingDirection': 'Rijrichting',
  'trackStudio.reverseDrivingDirection': 'Rijrichting omdraaien',
  'trackStudio.drivingDirectionDesc': 'Draai de rijrichting van het circuit om. Alle bochtvolgordes en tijdsectoren worden automatisch aangepast.',
  'trackStudio.multiplePointsSelected': '{count} punten geselecteerd',
  'trackStudio.selectAll': 'Alles selecteren (Cmd+A)',
  'trackStudio.deselectAll': 'Deselecteren (Esc)',
  'trackStudio.deleteSelectedPoints': '{count} punten verwijderen',
  'trackStudio.customWidthSelection': 'Aangepaste breedte (alle geselecteerde)',
  'tracks.reanalyzeLaps': 'Circuitronden opnieuw analyseren',
  'tracks.reanalyzeAllLaps': 'Alle ronden opnieuw analyseren',
  'tracks.reanalyzeQueued': '{enqueued} ronde(n) in de wachtrij geplaatst voor analyse ({skipped} al up-to-date).',
  'tracks.reanalyzeForceQueued': '{enqueued} ronde(n) in de wachtrij geplaatst voor volledige herberekening.',
  'tracks.reanalyzeAllUpToDate': 'Alle {skipped} ronde(n) zijn al up-to-date met de huidige circuitgegevens.',
  'tracks.reanalyzeConfirmTitle': 'Circuitronden opnieuw analyseren',
  'tracks.reanalyzeConfirmMessage': 'De circuitdata-hash voor "{name}" is niet gewijzigd ({count} ronde(n) zijn al up-to-date). Wil je toch een volledige herberekening voor dit circuit forceren?',
  'tracks.reanalyzeConfirmButton': 'Ronden opnieuw analyseren',
  'tracks.reanalyzeAllConfirmTitle': 'Alle ronden opnieuw analyseren',
  'tracks.reanalyzeAllConfirmMessage': 'Weet je zeker dat je een volledige herberekening van alle ronden en sessies op alle circuits wilt starten?',

  /* ── Monitoring ────────────────────────────────────────────── */
  'tab.monitoring': 'Monitoring',
  'monitoring.title': 'Wachtrijmonitoring',
  'monitoring.subtitle': 'Realtime wachtrijstatistieken en verwerkingsopties voor ronde- en sessieanalyses',
  'monitoring.refresh': 'Vernieuwen',
  'monitoring.autoRefreshActive': 'Wordt elke 3s automatisch vernieuwd zolang actief',
  'monitoring.lapQueueTitle': 'Wachtrij rondeanalyse',
  'monitoring.sessionQueueTitle': 'Wachtrij sessieanalyse',
  'monitoring.pending': 'In afwachting',
  'monitoring.processing': 'Bezig met verwerken',
  'monitoring.completed': 'Voltooid',
  'monitoring.failed': 'Mislukt',
  'monitoring.totalEligible': 'Totaal in aanmerking komend',
  'monitoring.actionsTitle': 'Globale wachtrijbediening',
  'monitoring.syncUnanalyzed': 'Niet-geanalyseerd & verouderd synchroniseren',
  'monitoring.syncUnanalyzedDesc': 'Scan alle circuits en plaats niet-geanalyseerde ronden of ronden met verouderde circuitgeometrie in de wachtrij',
  'monitoring.syncUnanalyzedDone': '{enqueued} ronde(n) in de wachtrij geplaatst ({skipped} al up-to-date).',
  'monitoring.retryFailed': 'Mislukte taken opnieuw proberen',
  'monitoring.retryFailedDesc': 'Zet alle mislukte ronde- en sessieanalysetaken terug op in afwachting',
  'monitoring.retryFailedDone': '{retried} mislukte taak/taken teruggezet op in afwachting.',
  'monitoring.reanalyzeAll': 'Geforceerd alle ronden opnieuw analyseren',
  'monitoring.reanalyzeAllDesc': 'Forceer een volledige herberekening van alle ronden en sessies op alle circuits',
  'monitoring.userQueuesTitle': 'Wachtrijvoortgang per gebruiker (Round-Robin)',
  'monitoring.userQueuesSubtitle': 'De worker verwerkt taken in round-robin-volgorde over gebruikers, zodat grote batches andere gebruikers nooit blokkeren',
  'monitoring.userQueuesEmpty': 'Geen actieve of wachtende taken per gebruiker.',
  'monitoring.user': 'Gebruiker',
  'monitoring.systemQueue': 'Systeem / Globaal',
  'monitoring.lapsStatus': 'Rondetaken',
  'monitoring.sessionsStatus': 'Sessietaken',
  'monitoring.progress': 'Voortgang',
  'monitoring.tracksTitle': 'Analysedekking per circuit & herberekening',
  'monitoring.tracksSubtitle': 'Bekijk de dekking van rondeanalyses per hoofdcircuit en start een incrementele of geforceerde herberekening',
  'monitoring.tracksEmpty': 'Nog geen hoofdcircuits geconfigureerd.',
  'monitoring.trackCoverage': 'Dekking',
  'monitoring.upToDateLaps': 'Up-to-date',
  'monitoring.outdatedLaps': 'Verouderd',
  'monitoring.unanalyzedLaps': 'Niet geanalyseerd',
  'monitoring.shapeReady': '200 segmenten gereed',
  'monitoring.noShape': 'Geen gegenereerde segmenten',
  'monitoring.reanalyzeTrack': 'Synchroniseren / Opnieuw analyseren',
  'monitoring.forceReanalyzeTrack': 'Alles forceren',

  /* ── Terms & Conditions ────────────────────────────────────── */
  'terms.gateTitle': 'Algemene Voorwaarden',
  'terms.gateSubtitle': 'Bekijk en accepteer de nieuwste Algemene Voorwaarden om door te gaan.',
  'terms.updatedBannerTitle': 'Bijgewerkte Algemene Voorwaarden:',
  'terms.updatedBannerDesc': 'Onze Algemene Voorwaarden zijn bijgewerkt. Bekijk en accepteer de nieuwe versie om door te gaan.',
  'terms.checkboxLabel': 'Ik heb de Algemene Voorwaarden gelezen en ga hiermee akkoord',
  'terms.acceptButton': 'Accepteren & doorgaan',
  'terms.acceptingButton': 'Accepteren…',
  'terms.loadError': 'Kan de Algemene Voorwaarden niet laden. Controleer je verbinding en probeer het opnieuw.',
  'terms.acceptError': 'Het registreren van je acceptatie is mislukt. Probeer het opnieuw.',

  /* ── Search & Filter Bar ───────────────────────────────────── */
  'search.placeholder': 'Zoeken of filter toevoegen...',
  'search.button': 'Zoeken',
  'search.syntaxHelp': 'Hulp bij zoeksyntax',
  'search.title': 'Gids voor zoeksyntax',
  'search.subtitle': 'Filter en zoek met gestructureerde velden en booleaanse operatoren.',
  'search.closeDialog': 'Sluiten',
  'search.field.bareTextDesc': 'Zoeken in standaard tekstvelden',
  'search.operatorsTitle': 'Operatoren',
  'search.numericComparisons': 'Numerieke / datumvergelijkingen',
  'search.range': 'Bereik (inclusief)',
  'search.rangeDesc': 'Waarden tussen min en max vinden',
  'search.booleanLogic': 'Booleaanse logica',
  'search.booleanLogicDesc': 'Meerdere voorwaarden combineren',
  'search.booleanLogicUse': 'Combineer termen met',
  'search.booleanLogicOr': 'of',
  'search.booleanLogicGroup': 'en groepeer expressies met haakjes',
  'search.quotes': 'Waarden tussen aanhalingstekens',
  'search.quotesDesc': 'Zet waarden met meerdere woorden tussen aanhalingstekens (bijv. track:"South Garda")',
  'search.examplesTitle': 'Voorbeelden',

  'filter.operator.eq': 'is',
  'filter.operator.neq': 'is niet',
  'filter.operator.gt': 'groter dan',
  'filter.operator.gte': 'minstens',
  'filter.operator.lt': 'kleiner dan',
  'filter.operator.lte': 'hoogstens',
  'filter.operator.range': 'tussen',
  'filter.operator.contains': 'bevat',
  'filter.remove': 'Filter verwijderen',
  'filter.done': 'Klaar',
  'filter.clearInput': 'Invoer wissen',
  'filter.selectField': 'Filteren op veld',
  'filter.pressEnter': 'Druk op Enter om toe te passen',
  'filter.selectValues': 'Waarden selecteren',
  'filter.noMatchingOptions': 'Geen overeenkomende opties',

  'filter.bugs.bareSearchExample': 'Zoek in samenvatting, beschrijving of melder',
  'filter.bugs.searchSummary': 'Filter op bug-samenvatting',
  'filter.bugs.searchDescription': 'Filter op bug-beschrijving',
  'filter.bugs.searchReporter': 'Filter op naam of e-mail van melder',
  'filter.bugs.filterStatus': 'Filter op bugstatus',
  'filter.bugs.filterSeverity': 'Filter op ernstniveau',
  'filter.bugs.filterPlatform': 'Filter op platform (web, ios, android)',
  'filter.bugs.filterDate': 'Filter op meldingsdatum',
  'filter.bugs.exampleOpenHigh': 'Openstaande bugs met hoge ernst',
  'filter.bugs.exampleMobileLogin': 'Mobiele inlogproblemen',

  'filter.users.bareSearchExample': 'Zoek op naam, e-mail of gebruikers-ID',
  'filter.users.searchName': 'Filter op weergavenaam',
  'filter.users.searchEmail': 'Filter op e-mailadres',
  'filter.users.searchId': 'Filter op gebruikers-ID',
  'filter.users.filterRole': 'Filter op toegewezen rol',
  'filter.users.exampleAdmin': 'Gebruikers met de beheerdersrol',
  'filter.users.exampleMultiRole': 'Beheerders of editors op naam',

  'filter.races.bareSearchExample': 'Zoek op seizoen, circuit, evenement of rijder',
  'filter.races.searchSeason': 'Filter op kampioenschapsseizoen',
  'filter.races.filterYear': 'Filter op kampioenschapsjaar',
  'filter.races.searchCountry': 'Filter op landcode',
  'filter.races.searchCategory': 'Filter op racecategorie',
  'filter.races.searchTrack': 'Filter op circuitnaam',
  'filter.races.searchEvent': 'Filter op evenementnaam',
  'filter.races.filterRound': 'Filter op rondenummer',
  'filter.races.searchSession': 'Filter op sessienaam',
  'filter.races.filterType': 'Filter op sessietype',
  'filter.races.searchDriver': 'Filter op rijdersnaam',
  'filter.races.typePractice': 'Vrije training',
  'filter.races.typeQualifying': 'Kwalificatie',
  'filter.races.typeHeat': 'Kwalificatieheat',
  'filter.races.typePrefinal': 'Prefinale',
  'filter.races.typeFinal': 'Finale',
  'filter.races.exampleRokTrack': 'ROK Cup-evenementen op South Garda',
  'filter.races.exampleFinalDriver': 'Finalesessies met een rijder',

  'races.season': 'Seizoen',
  'races.year': 'Jaar',
  'races.country': 'Land',
  'races.event': 'Evenement',
  'races.roundLabel': 'Ronde',
  'races.session': 'Sessie',
  'races.type': 'Type',
  'races.label': 'Label',
  'races.time': 'Tijd',

  'trackStudio.zoomIn': 'Inzoomen',
  'trackStudio.zoomOut': 'Uitzoomen',
  'trackStudio.resetView': 'Weergave herstellen',
  'trackStudio.geoLocation': 'Geolocatie',
  'trackStudio.finishLineTitle': 'Start- / finishlijn',
  'trackStudio.finishLineHint': 'Sleep de vlag over het circuit op het canvas, klik op het circuit of gebruik de schuifregelaar hieronder.',
  'trackStudio.staticGpsPosition': 'Statische GPS-positie',
  'trackStudio.distance': 'Afstand',
  'trackStudio.previous': 'Vorige',
  'trackStudio.next': 'Volgende',

  'users.terms': 'Voorwaarden',
  'users.termsAccepted': 'Geaccepteerd',
  'users.termsOutdated': 'Verouderd',
  'users.termsPending': 'In afwachting',
  'users.resetTerms': 'Algemene voorwaarden resetten',
  'users.confirmResetTerms': 'Acceptatie van de algemene voorwaarden voor {name} resetten? De gebruiker moet de algemene voorwaarden opnieuw bekijken en accepteren voordat hij of zij verder kan gaan.',
  'users.termsReset': 'Acceptatie van algemene voorwaarden gereset voor {name}',
  // Filter Schema & UI Translations
  'filter.filterBy': 'Filteren op {label}',
  'filter.removeFilter': 'Filter {label} verwijderen',
  'filter.prevMonth': 'Vorige maand',
  'filter.nextMonth': 'Volgende maand',
  'filter.selectStartDate': 'Selecteer startdatum in kalender',
  'filter.op.exact': 'Exact',
  'filter.op.atLeast': 'Minstens',
  'filter.op.atMost': 'Hoogstens',
  'filter.op.between': 'Tussen',
  'filter.op.greater': 'Groter',
  'filter.op.less': 'Kleiner',
  'filter.preset.today': 'Vandaag',
  'filter.preset.last7d': 'Laatste 7 dagen',
  'filter.preset.last30d': 'Laatste 30 dagen',
  'filter.preset.last90d': 'Laatste 90 dagen',
  'filter.preset.last1y': 'Afgelopen jaar',
  'filter.preset.last7dShort': 'Laatste 7d',
  'filter.preset.last30dShort': 'Laatste 30d',
  'filter.preset.last90dShort': 'Laatste 90d',
  'filter.preset.last1yShort': 'Laatste 1j',
  'filter.sessionType.race': 'Race',
  'filter.sessionType.final': 'Finale',
  'filter.sessionType.pre_final': 'Pre-finale',
  'filter.sessionType.heat': 'Heat',
  'filter.sessionType.qualifying': 'Kwalificatie',
  'filter.unit.laps': 'ronden',
  'filter.field.date': 'Datum',
  'filter.field.track': 'Circuit',
  'filter.desc.track': 'Komt overeen met circuitnaam',
  'filter.field.championship': 'Kampioenschap',
  'filter.desc.championship': 'Naam van het kampioenschap',
  'filter.field.category': 'Categorie',
  'filter.desc.category': 'Canonieke racecategorie',
  'filter.field.sessionType': 'Sessietype',
  'filter.desc.sessionType': 'Sessieclassificatie (race, heat, kwalificatie, finale)',
  'filter.field.laps': 'Ronden',
  'filter.field.season': 'Seizoen',
  'filter.desc.season': 'Komt overeen met het seizoensjaar',
  'filter.field.driver': 'Rijder',
  'filter.placeholder.driver': 'bijv. Yanick',
  'filter.placeholder.email': 'bijv. gmail.com',
  'filter.desc.email': 'Komt overeen met het e-mailadres',
  'bugs.severity': 'Ernst',
  'bugs.severityCritical': 'Kritiek',
  'bugs.severityHigh': 'Hoog',
  'bugs.severityMedium': 'Gemiddeld',
  'bugs.severityLow': 'Laag',
  'bugs.statusInProgress': 'In behandeling',
  'bugs.statusClosed': 'Gesloten',
  'bugs.submitter': 'E-mail indiener',
  'filter.bugs.title': 'Bugrapport-zoeksyntax',
  'filter.bugs.subtitle': 'Gemelde bugs filteren op status, ernst, e-mail of trefwoorden.',
  'filter.field.bugTitle': 'Samenvatting / Titel',
  'filter.placeholder.bugTitle': 'bijv. crash, upload',
  'filter.desc.bugTitle': 'Komt overeen met titel of beschrijving van het probleem',
  'filter.desc.bugStatus': 'Status van het probleem (open, in behandeling, gesloten)',
  'filter.desc.bugSeverity': 'Ernstniveau van de bug',
  'filter.placeholder.bugEmail': 'bijv. gebruiker@voorbeeld.nl',
  'filter.desc.bugEmail': 'Komt overeen met het e-mailadres van de indiener',
  'filter.field.bugEnv': 'Omgeving',
  'filter.desc.bugEnv': 'Filteren op meldingsomgeving (prod, dev, local)',
  'filter.bugs.example1': 'Alle openstaande bugrapporten',
  'filter.bugs.example2': 'Openstaande problemen in productie',
  'filter.bugs.example3': 'Openstaande crashrapporten',
  'filter.field.driverEntries': 'Aantal deelnemers',
  'filter.desc.driverEntries': 'Totaal aantal deelnemende rijders in de race',
  'filter.preset.entriesSmall': 'Klein (< 10)',
  'filter.preset.entriesMedium': 'Gemiddeld (10..25)',
  'filter.preset.entriesLarge': 'Groot (> 25)',
  'filter.desc.raceLaps': 'Aantal voltooide raceronden',
  'filter.desc.raceDriver': 'Komt overeen met de naam van de rijder in de raceresultaten',
  'filter.desc.raceDate': 'Races filteren op periode of specifiek datumbereik',
  'filter.races.example1': 'Races in 2026 op Wohlen',
  'filter.races.example2': 'Grote startvelden in Senior MAX',
  'filter.races.example3': 'Races met Yanick in het afgelopen jaar',
  'filter.users.title': 'Gebruikers-zoeksyntax',
  'filter.users.subtitle': 'Geregistreerde gebruikers filteren op naam, e-mail, rol of authenticatieprovider.',
  'users.colName': 'Naam',
  'users.colEmail': 'E-mail',
  'users.colRole': 'Rol',
  'users.colProvider': 'Authenticatieprovider',
  'users.roleAdmin': 'Beheerder',
  'users.roleOwner': 'Eigenaar',
  'users.roleUser': 'Gebruiker',
  'filter.desc.userName': 'Komt overeen met de weergavenaam van de gebruiker',
  'filter.desc.userRole': 'Gebruikersrol (beheerder, eigenaar, gebruiker)',
  'filter.desc.authProvider': 'Authenticatieprovider',
  'filter.provider.password': 'Wachtwoord',
  'filter.desc.userDate': 'Aanmaak- of laatst actieve datum',
  'filter.users.example1': 'Gebruikers zoeken die overeenkomen met Yanick',
  'filter.users.example2': 'Platformbeheerders',
  'filter.users.example3': 'Google-logins in de afgelopen 30 dagen',
};

export default nl;

